/**
 * Auth Metadata Utilities
 *
 * Helper functions and constants for working with user metadata.
 */

import type { AuthUser, UserMetadata } from './types';

/**
 * Get metadata value by key from user metadata array
 */
export function getMetaValue(metadata: readonly UserMetadata[], key: string): string | undefined {
  return metadata.find((m) => m.key === key)?.value;
}

/**
 * Account metadata keys
 * Maps human-readable names to Saleor metadata keys.
 */
export const ACCOUNT_METADATA = {
  /** Date of birth */
  dob: 'user.dob',
  /** Newsletter consent */
  newsletterConsent: 'user.newsletterConsent',
  /** Boutique communication opt-in */
  boutiqueCommunicationOptin: 'user.btqOptIn',
  /** Personal title (Mr, Mrs, etc) */
  personalTitle: 'user.personalTitle',
  /** Phone number */
  phone: 'user.phone',
  /** Phone prefix/country code */
  phonePrefix: 'user.phonePrefix',
  /** User's preferred language */
  language: 'user.language',
  /** User's country */
  country: 'user.country',
  /** Whether address is saved */
  addressSaved: 'saved',
  /** Account source (storefront, checkout, etc) */
  accountSource: 'Online_Account_source__c',
  /** Brand used when the account was created (BREITLING | GALLET) */
  brandUsedAtCreation: 'user.brand_used_at_creation',
  /** Brand used when the account was last modified (BREITLING | GALLET) */
  brandUsedAtModify: 'user.brand_used_at_modify',
  /** First name in Kanji (Japan) */
  first_name_kanji: 'user.first_name_kanji',
  /** Last name in Kanji (Japan) */
  last_name_kanji: 'user.last_name_kanji',
  /** First name in Romaji (Japan) */
  first_name_romaji: 'user.first_name_romaji',
  /** Last name in Romaji (Japan) */
  last_name_romaji: 'user.last_name_romaji',
  /** First name in Katakana (Japan) */
  first_name_katakana: 'user.first_name_katakana',
  /** Last name in Katakana (Japan) */
  last_name_katakana: 'user.last_name_katakana',
  /** House/mansion name (Japan addresses) */
  houseMansionName: 'address.house_mansion',
  /** Apartment number */
  apartmentNumber: 'address.apartment_number',
  /** VAT number */
  vatNumber: 'address.vatNumber',
  /** Australian Business Number */
  abn: 'address.abn',
  /** Whether user is a club member */
  isClubMember: 'user.is_club_member',
  /** Whether user is affiliated */
  isAffiliated: 'user.is_affiliated',
  /** SalesForce account record ID (Breitling preference-center identifier) */
  salesForceId: 'user.salesforce_id',
  /**
   * Salesforce-generated opaque identifier (Account.UUID__c) used by the
   * Gallet preference center instead of exposing the record id.
   */
  galletPreferenceUuid: 'user.gallet_pref_uuid',
  /**
   * Checkout UTM metadata written before orderCreate (Breitling parity / BEC-20520).
   * Saleor copies these onto the order as `client.utm_*`.
   */
  checkoutUtmSource: 'client.utm_source',
  checkoutUtmMedium: 'client.utm_medium',
  checkoutUtmCampaign: 'client.utm_campaign',
  /**
   * User registration UTM metadata (SFDC/CPI reads `user.utm_*`).
   */
  utmSource: 'user.utm_source',
  utmMedium: 'user.utm_medium',
  utmCampaign: 'user.utm_campaign',
} as const;

/** Known metadata values paired with {@link ACCOUNT_METADATA} keys. */
export const ACCOUNT_METADATA_VALUE = {
  addressSaved: 'TRUE',
} as const;

/** Pre-canonical camelCase keys from an older EditDetailsForm. Read fallback only. */
const LEGACY_JAPANESE_NAME_METADATA = {
  firstNameKanji: 'user.firstNameKanji',
  lastNameKanji: 'user.lastNameKanji',
  firstNameKatakana: 'user.firstNameKatakana',
  lastNameKatakana: 'user.lastNameKatakana',
} as const;

/** Bare keys written by checkout JP shipping metadata. Read fallback only. */
const CHECKOUT_JAPANESE_NAME_METADATA = {
  firstNameKanji: 'firstNameKanji',
  lastNameKanji: 'lastNameKanji',
  firstNameKatakana: 'firstNameKatakana',
  lastNameKatakana: 'lastNameKatakana',
  firstNameRomaji: 'firstNameRomaji',
  lastNameRomaji: 'lastNameRomaji',
} as const;

export type JapaneseNameFields = {
  firstNameKanji?: string | null;
  lastNameKanji?: string | null;
  firstNameKatakana?: string | null;
  lastNameKatakana?: string | null;
  firstNameRomaji?: string | null;
  lastNameRomaji?: string | null;
};

/** Saleor metadata entries for JP names, using canonical {@link ACCOUNT_METADATA} keys. */
export function japaneseNameMetadata(
  names: JapaneseNameFields
): Array<{ key: string; value: string }> {
  const entries: Array<{ key: string; value: string }> = [];
  const push = (key: string, value?: string | null) => {
    const trimmed = value?.trim();
    if (trimmed) entries.push({ key, value: trimmed });
  };
  push(ACCOUNT_METADATA.first_name_kanji, names.firstNameKanji);
  push(ACCOUNT_METADATA.last_name_kanji, names.lastNameKanji);
  push(ACCOUNT_METADATA.first_name_katakana, names.firstNameKatakana);
  push(ACCOUNT_METADATA.last_name_katakana, names.lastNameKatakana);
  push(ACCOUNT_METADATA.first_name_romaji, names.firstNameRomaji);
  push(ACCOUNT_METADATA.last_name_romaji, names.lastNameRomaji);
  return entries;
}

function readJapaneseNameValue(metadata: readonly UserMetadata[], keys: readonly string[]): string {
  for (const key of keys) {
    const value = getMetaValue(metadata, key);
    if (value) return value;
  }
  return '';
}

/** Reads JP names from metadata, preferring canonical keys then legacy/checkout fallbacks. */
export function getJapaneseNameFromMetadata(metadata: readonly UserMetadata[]): {
  firstNameKanji: string;
  lastNameKanji: string;
  firstNameKatakana: string;
  lastNameKatakana: string;
  firstNameRomaji: string;
  lastNameRomaji: string;
} {
  return {
    firstNameKanji: readJapaneseNameValue(metadata, [
      ACCOUNT_METADATA.first_name_kanji,
      LEGACY_JAPANESE_NAME_METADATA.firstNameKanji,
      CHECKOUT_JAPANESE_NAME_METADATA.firstNameKanji,
    ]),
    lastNameKanji: readJapaneseNameValue(metadata, [
      ACCOUNT_METADATA.last_name_kanji,
      LEGACY_JAPANESE_NAME_METADATA.lastNameKanji,
      CHECKOUT_JAPANESE_NAME_METADATA.lastNameKanji,
    ]),
    firstNameKatakana: readJapaneseNameValue(metadata, [
      ACCOUNT_METADATA.first_name_katakana,
      LEGACY_JAPANESE_NAME_METADATA.firstNameKatakana,
      CHECKOUT_JAPANESE_NAME_METADATA.firstNameKatakana,
    ]),
    lastNameKatakana: readJapaneseNameValue(metadata, [
      ACCOUNT_METADATA.last_name_katakana,
      LEGACY_JAPANESE_NAME_METADATA.lastNameKatakana,
      CHECKOUT_JAPANESE_NAME_METADATA.lastNameKatakana,
    ]),
    firstNameRomaji: readJapaneseNameValue(metadata, [
      ACCOUNT_METADATA.first_name_romaji,
      CHECKOUT_JAPANESE_NAME_METADATA.firstNameRomaji,
    ]),
    lastNameRomaji: readJapaneseNameValue(metadata, [
      ACCOUNT_METADATA.last_name_romaji,
      CHECKOUT_JAPANESE_NAME_METADATA.lastNameRomaji,
    ]),
  };
}

/**
 * Order-flow metadata keys.
 * Stored on the checkout/order so post-order backend processes can read
 * the user's intent (account creation, watch registration, etc.).
 */
export const ORDER_FLOW_METADATA = {
  wasUserAuthenticated: 'orderFlow.wasUserAuthenticated',
  wasRegisterMyWatchChecked: 'orderFlow.wasRegisterMyWatchChecked',
  wasAccountCreated: 'orderFlow.wasAccountCreated',
  wasCreateMyAccountChecked: 'orderFlow.wasCreateMyAccountWithoutWatchChecked',
  userJoinClub: 'orderFlow.userJoinClub',
  userAffiliated: 'orderFlow.userAffiliated',
  receiveReceipt: 'orderFlow.japanReceipt',
} as const;

/** Account source used when a guest creates an account during checkout. */
export const ONLINE_ACCOUNT_SOURCE = {
  STOREFRONT_CHECKOUT: 'storefront_checkout',
} as const;

/**
 * Get the user's SalesForce ID (convenience function)
 *
 * @param user - AuthUser object
 * @returns SalesForce ID or undefined
 */
export function getSalesForceId(user: AuthUser | null): string | undefined {
  if (!user) return undefined;
  return getMetaValue(user.metadata, ACCOUNT_METADATA.salesForceId);
}

/**
 * Get the user's Gallet preference-center UUID (convenience function)
 *
 * @param user - AuthUser object
 * @returns Gallet preference-center UUID (Account.UUID__c) or undefined
 */
export function getGalletPreferenceUuid(user: AuthUser | null): string | undefined {
  if (!user) return undefined;
  return getMetaValue(user.metadata, ACCOUNT_METADATA.galletPreferenceUuid);
}
